import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { getContext } from '@adornis/baseql/server/context.js';
import { type AuthenticationContext, refreshAccountAccess, isAuthenticatedContext } from '../server/auth.js';
import { CallSession } from './CallSession.js';
import type { WorkTask } from './work-task-types.js';

/** A read-only, paged index. Copying never invokes the AI analysis or modifies feedback. */
export const workTaskAnalysisIndex = registerQuery({
  type: () => String,
  operationName: 'workTaskAnalysisIndex',
  params: [{ name: 'offset', type: () => Int }],
  resolve: (offsetArg: number) => async () => {
    const context = getContext() as AuthenticationContext | undefined;
    if (!context || context.serverContext || typeof context.userID !== 'string' || !context.userID)
      throw new Error('TASK_FORBIDDEN');
    await refreshAccountAccess(context);
    if (!isAuthenticatedContext(context)) throw new Error('TASK_FORBIDDEN');
    if (Object.prototype.toString.call(offsetArg) !== '[object Number]') throw new Error('TASK_INVALID_INPUT');
    const offset = Number(offsetArg);
    if (!Number.isSafeInteger(offset) || offset < 0 || offset > 1000000) throw new Error('TASK_INVALID_INPUT');
    const pageSize = 100;
    const tasks = await getRawCollection<WorkTask>('work_tasks');
    const sessions = await getCollection(CallSession);
    const [taskRows, feedbackRows] = await Promise.all([
      tasks
        .find({}, { projection: { _id: 1, title: 1, feedbackLinks: 1 }, maxTimeMS: 5000 })
        .sort({ _id: 1 })
        .skip(offset)
        .limit(pageSize + 1)
        .toArray(),
      sessions
        .find(
          { testerNotes: { $type: 'string', $regex: '\\S' } },
          {
            projection: { _id: 0, sessionID: 1, callUuid: 1, testerName: 1, testerNotes: 1, testerNotesUpdatedAt: 1 },
            maxTimeMS: 5000,
          },
        )
        .sort({ _id: 1 })
        .skip(offset)
        .limit(pageSize + 1)
        .toArray(),
    ]);
    return JSON.stringify({
      tasks: taskRows.slice(0, pageSize),
      feedback: feedbackRows.slice(0, pageSize).map(row => ({
        callID: String(row.sessionID || row.callUuid || ''),
        testerName: row.testerName ?? '',
        notes: row.testerNotes ?? '',
        updatedAt: row.testerNotesUpdatedAt ?? null,
      })),
      nextOffset: taskRows.length > pageSize || feedbackRows.length > pageSize ? offset + pageSize : null,
    });
  },
});
